import { faPlus, faTag } from '@fortawesome/pro-regular-svg-icons';
import { Box } from '@mui/system';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button';
import { Dialog } from 'src/components/Dialog';
import { Divider } from 'src/components/Divider';
import { Icon } from 'src/components/Icon';
import { Skeleton } from 'src/components/Skeleton';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { UserSelectorChip } from 'src/pages/Clients/PaymentDialog/UserSelectorChip';
import { formatCurrency } from 'src/utils/numberFormatter';

import { usePosContext } from '../PosContext';

import { AddDiscoutDialog } from './AddDiscoutDialog';
import CartSummaryRow from './CartSummayRow';
import PaymentMethodsDialog from './PaymentMenthodsDialog';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { PosCartCard } from './PosCartCard';

interface SummaryDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function SummaryDialog({ open, onClose }: SummaryDialogProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pointOfSale.summary',
  });

  const [discountsAccordionOpen, setDiscountsAccordionOpen] = useState(false);
  const [taxesAccordionOpen, setTaxesAccordionOpen] = useState(false);

  const {
    totalItems,
    selectedUser,
    cart,
    removeFromCart,
    updateQuantity,
    addDiscountDialogOpen,
    setAddDiscountDialogOpen,
    paymentMethod,
    paymentMethodsDialogOpen,
    setPaymentMethodsDialogOpen,
    charge,
    isGuestCheckout,
    setShowWelcomeDialog,
    handleChangePaymentMethod,
    guestCheckoutEnabled,
    setShowFindAccountDialog,
    shouldDisableCheckout,
    isLoadingIntent,
    cartSummary,
  } = usePosContext();

  const handleChangeUser = () => {
    if (!guestCheckoutEnabled) {
      setShowFindAccountDialog(true);
    } else {
      setShowWelcomeDialog(true);
    }
  };

  const { subtotal, tax, taxes, total, accountBalance, discount, discounts } =
    useMemo(() => {
      return {
        subtotal: formatCurrency(cartSummary.subtotal, {
          cents: true,
          currency: cartSummary.currency,
        }),
        tax: formatCurrency(cartSummary.tax, {
          cents: true,
          currency: cartSummary.currency,
        }),
        total: formatCurrency(cartSummary.total, {
          cents: true,
          currency: cartSummary.currency,
        }),
        accountBalance: formatCurrency(cartSummary.accountBalance, {
          cents: true,
          currency: cartSummary.currency,
        }),
        discount: formatCurrency(cartSummary.discount, {
          cents: true,
          currency: cartSummary.currency,
        }),
        taxes: Array.from(cartSummary.taxes).map(([key, value]) => ({
          label: key,
          value: formatCurrency(value, {
            cents: true,
            currency: cartSummary.currency,
          }),
        })),
        discounts: Array.from(cartSummary.discounts).map(([key, value]) => ({
          couponCode: key,
          value: formatCurrency(value, {
            cents: true,
            currency: cartSummary.currency,
          }),
        })),
      };
    }, [cartSummary]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('title')}
      maxWidth="md"
      fullWidth
    >
      <Stack
        justifyContent="space-between"
        alignItems="center"
        sx={{ height: '100%' }}
      >
        <Stack p={2} sx={{ width: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <UserSelectorChip
                user={selectedUser}
                onChangeUser={handleChangeUser}
              />
            </Stack>

            <Box
              sx={{
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: '100px',
                py: 0.5,
                px: 1.5,
              }}
            >
              {t('items', { count: totalItems })}
            </Box>
          </Stack>
          <Divider sx={{ mt: 2 }} />
        </Stack>

        <Stack
          p={2}
          spacing={1}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
          }}
        >
          {cart?.cartItems.map((item, index) => (
            <PosCartCard
              key={item.id?.toString() || index}
              item={item}
              cartItemIndex={index}
              removeFromCart={removeFromCart}
              updateQuantity={updateQuantity}
            />
          ))}
        </Stack>

        <Stack spacing={1} p={2} width="100%">
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">{t('addDiscount')}</Typography>
              <Button
                variant="borderless"
                color="primary"
                onClick={() => setAddDiscountDialogOpen(true)}
              >
                <Icon icon={faPlus} />
              </Button>
            </Stack>
          </Stack>

          <Stack sx={{ my: 1 }}>
            <PaymentMethodSelector
              isGuestCheckout={isGuestCheckout}
              paymentMethod={paymentMethod}
              onPaymentMethodClick={() => setPaymentMethodsDialogOpen(true)}
            />
          </Stack>

          <CartSummaryRow
            label={t('subtotal')}
            value={subtotal}
            isLoading={isLoadingIntent}
          />

          {discounts.length > 0 && (
            <>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Button
                  variant="borderless"
                  sx={{ p: 0, m: 0 }}
                  onClick={() =>
                    setDiscountsAccordionOpen(!discountsAccordionOpen)
                  }
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      textDecoration: 'underline',
                    }}
                  >
                    {t('discounts')}
                  </Typography>
                </Button>
                {isLoadingIntent ? (
                  <Skeleton variant="text" width={56} height={18} />
                ) : (
                  <Typography variant="h6">{discount}</Typography>
                )}
              </Stack>

              {discountsAccordionOpen && (
                <Stack>
                  {discounts.map((item) => (
                    <Stack
                      key={item.couponCode}
                      ml={1}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Icon size="small" color="info" icon={faTag} />
                        <Typography variant="caption" color="text.secondary">
                          {item.couponCode}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {item.value}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </>
          )}

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Button
              variant="borderless"
              sx={{ p: 0, m: 0 }}
              onClick={() => setTaxesAccordionOpen(!taxesAccordionOpen)}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  textDecoration: taxes.length > 0 ? 'underline' : 'none',
                }}
              >
                {t('taxes')}
              </Typography>
            </Button>
            {isLoadingIntent ? (
              <Skeleton variant="text" width={56} height={18} />
            ) : (
              <Typography variant="h6">{tax}</Typography>
            )}
          </Stack>

          {taxesAccordionOpen && (
            <Stack>
              {taxes.map((item) => (
                <Stack
                  key={item.label}
                  ml={1}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icon size="small" color="info" icon={faTag} />
                    <Typography variant="caption" color="text.secondary">
                      {item.label}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      p={0}
                      m={0}
                    >
                      {item.value}
                    </Typography>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}

          {cartSummary.accountBalance !== 0 && (
            <CartSummaryRow
              label={t('accountBalance')}
              value={accountBalance}
              isLoading={isLoadingIntent}
            />
          )}

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{
              mt: 2,
            }}
          >
            <Typography variant="h6">{t('total')}</Typography>
            <Typography variant="h6">{total}</Typography>
          </Stack>

          <Button
            sx={{
              mt: 2,
            }}
            variant="prominent"
            color="primary"
            disabled={shouldDisableCheckout}
            fullWidth
            onClick={() => {
              onClose();
              charge();
            }}
          >
            {t('charge', {
              amount: total,
            })}
          </Button>
        </Stack>
      </Stack>
      <AddDiscoutDialog
        open={addDiscountDialogOpen}
        onClose={() => setAddDiscountDialogOpen(false)}
      />
      <PaymentMethodsDialog
        open={paymentMethodsDialogOpen}
        onClose={() => setPaymentMethodsDialogOpen(false)}
        user={selectedUser}
        setPaymentMethod={handleChangePaymentMethod}
      />
    </Dialog>
  );
}
